import { useCallback, useEffect, useState } from "react";
import {
    Card, Table, Tag, Button, Space, Typography, Tabs, Modal, Input, DatePicker, Upload,
    message, Select, Empty, Popconfirm, Row, Col, Statistic,
} from "antd";
import {
    ClusterOutlined, PlusOutlined, UploadOutlined, UserAddOutlined, DeleteOutlined,
    FileTextOutlined, CheckCircleOutlined, TeamOutlined, EditOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import axiosInstance from "../../variables/api/axiosInstance";
import { LOGGED_USER } from "../../variables/constants";
import { rightsService } from "../../services/rightsService";
import SideBar from "../../components/SideBar";
import NavBar from "../../components/NavBar";
import Footer from "../../components/Footer";

const { Text, Title } = Typography;
const PRIMARY = "#242166";
const TYPE = "PRODUCTION";
const SEANCE_STATUT = { PLANIFIEE: "blue", TENUE: "gold", CLOTUREE: "default" };
const isPool = () => (LOGGED_USER?.profil?.organisme?.typeOrganisme || "").toUpperCase() === "POOL";
const isMembre = () => isPool() || rightsService("HSI111");
const myOrgCode = () => LOGGED_USER?.profil?.organisme?.code;

/**
 * Commission Technique PRODUCTION : séances documentées (objet, observations, PV joint accessible à
 * tous les participants) + paramétrage des participants par compagnie. Pas de dossier / seuil / blocage.
 */
const CommissionProduction = () => {
    const [seances, setSeances] = useState([]);
    const [participants, setParticipants] = useState([]);
    const [compagnies, setCompagnies] = useState([]);

    const charger = useCallback(() => {
        Promise.all([
            axiosInstance.get("/api/commission-technique/seances").then((r) => setSeances((r.data || []).filter((s) => (s.type || "SINISTRE") === TYPE))).catch(() => {}),
            axiosInstance.get("/api/commission-technique/participants", { params: { type: TYPE } }).then((r) => setParticipants(r.data || [])).catch(() => {}),
            axiosInstance.get("/api/organisme/assurance").then((r) => setCompagnies(r.data || [])).catch(() => {}),
        ]);
    }, []);
    useEffect(() => { charger(); }, [charger]);

    const creerSeance = () => {
        let date = dayjs(); let objet = ""; let observations = "";
        Modal.confirm({
            title: "Nouvelle séance — Commission Technique Production",
            content: (
                <Space direction="vertical" style={{ width: "100%", marginTop: 8 }}>
                    <DatePicker style={{ width: "100%" }} defaultValue={date} format="DD/MM/YYYY" onChange={(d) => { date = d; }} />
                    <Input.TextArea rows={2} placeholder="Objet / ordre du jour" onChange={(e) => { objet = e.target.value; }} />
                    <Input.TextArea rows={3} placeholder="Observations de la séance (optionnel)" onChange={(e) => { observations = e.target.value; }} />
                </Space>
            ),
            okText: "Créer", cancelText: "Annuler",
            onOk: () => axiosInstance.post("/api/commission-technique/seances", { dateSeance: (date || dayjs()).format("YYYY-MM-DD"), objet, observations, type: TYPE })
                .then(() => { message.success("Séance créée."); charger(); })
                .catch((e) => message.error(e?.response?.data?.message || "Échec.")),
        });
    };
    const cloturer = (s) => Modal.confirm({
        title: `Clôturer la séance ${s.numeroSeance} ?`, okText: "Clôturer", okButtonProps: { danger: true }, cancelText: "Annuler",
        onOk: () => axiosInstance.put(`/api/commission-technique/seances/${s.codeId}/cloturer`)
            .then(() => { message.success("Séance clôturée."); charger(); })
            .catch((e) => message.error(e?.response?.data?.message || "Échec.")),
    });
    const marquerTenue = (s) => axiosInstance.put(`/api/commission-technique/seances/${s.codeId}/tenue`)
        .then(() => { message.success("Séance marquée tenue."); charger(); })
        .catch((e) => message.error(e?.response?.data?.message || "Échec."));
    const editerSeance = (s) => {
        let objet = s.objet || ""; let observations = s.observations || "";
        Modal.confirm({
            title: `Séance ${s.numeroSeance} — objet & observations`,
            content: (
                <Space direction="vertical" style={{ width: "100%", marginTop: 8 }}>
                    <Input.TextArea rows={2} defaultValue={objet} placeholder="Objet" onChange={(e) => { objet = e.target.value; }} />
                    <Input.TextArea rows={4} defaultValue={observations} placeholder="Observations" onChange={(e) => { observations = e.target.value; }} />
                </Space>
            ),
            okText: "Enregistrer", cancelText: "Annuler",
            onOk: () => axiosInstance.put(`/api/commission-technique/seances/${s.codeId}`, { objet, observations })
                .then(() => { message.success("Séance mise à jour."); charger(); })
                .catch((e) => message.error(e?.response?.data?.message || "Échec.")),
        });
    };
    // Téléchargement d'un PV (accessible aux participants).
    const telechargerPv = (chemin) => {
        const filename = (chemin || "").split("/").pop();
        axiosInstance.get(`/api/commission-technique/pv/${encodeURIComponent(filename)}`, { responseType: "blob" })
            .then((r) => {
                const url = window.URL.createObjectURL(new Blob([r.data]));
                const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
                window.URL.revokeObjectURL(url);
            })
            .catch(() => message.error("Téléchargement impossible."));
    };

    const seanceColumns = [
        { title: "N° séance", dataIndex: "numeroSeance" },
        { title: "Date", dataIndex: "dateSeance", render: (v) => v ? dayjs(v).format("DD/MM/YYYY") : "-" },
        { title: "Objet", dataIndex: "objet", render: (v) => v || "-" },
        { title: "Observations", dataIndex: "observations", render: (v) => v ? <Text style={{ fontSize: 12 }}>{v}</Text> : <Text type="secondary">—</Text> },
        { title: "Statut", dataIndex: "statut", width: 100, render: (v) => <Tag color={SEANCE_STATUT[v]}>{v}</Tag> },
        {
            title: "PV / comptes rendus", dataIndex: "comptesRendus",
            render: (v) => (v || []).length
                ? <Space wrap>{(v || []).map((cr, i) => <Button key={i} size="small" type="link" style={{ padding: "0 4px" }} icon={<FileTextOutlined />} onClick={() => telechargerPv(cr)}>{cr.split("/").pop()}</Button>)}</Space>
                : <Text type="secondary">—</Text>,
        },
        {
            title: "Actions", key: "a", width: 340, render: (_, s) => isMembre() && s.statut !== "CLOTUREE" ? (
                <Space wrap>
                    <Button size="small" icon={<EditOutlined />} onClick={() => editerSeance(s)}>Éditer</Button>
                    {s.statut === "PLANIFIEE" && <Button size="small" icon={<CheckCircleOutlined />} onClick={() => marquerTenue(s)}>Tenue</Button>}
                    <Upload showUploadList={false} multiple
                        customRequest={({ file, onSuccess, onError }) => {
                            const fd = new FormData(); fd.append("fichiers", file);
                            axiosInstance.post(`/api/commission-technique/seances/${s.codeId}/comptes-rendus`, fd)
                                .then(() => { onSuccess?.(); message.success("PV joint."); charger(); })
                                .catch((e) => { onError?.(e); message.error("Échec de l'upload."); });
                        }}>
                        <Button size="small" icon={<UploadOutlined />}>Joindre PV</Button>
                    </Upload>
                    <Button size="small" danger onClick={() => cloturer(s)}>Clôturer</Button>
                </Space>
            ) : null,
        },
    ];

    const nbPv = seances.reduce((n, s) => n + ((s.comptesRendus || []).length), 0);

    return (
        <div className="layout-wrapper layout-content-navbar">
            <div className="layout-container">
                <SideBar />
                <div className="layout-page">
                    <NavBar />
                    <div className="content-wrapper" style={{ background: "#f0f2f5" }}>
                        <div className="container-xxl flex-grow-1 container-p-y">
                            <div style={{ background: `linear-gradient(135deg, ${PRIMARY} 0%, #3b3a8a 100%)`, borderRadius: 16, padding: "20px 24px", marginBottom: 16, color: "#fff" }}>
                                <Title level={3} style={{ color: "#fff", margin: 0 }}><ClusterOutlined /> Commission Technique Production</Title>
                                <Text style={{ color: "rgba(255,255,255,0.85)" }}>Séances de production : objet, observations et PV joints, accessibles aux participants.</Text>
                            </div>
                            <Row gutter={[12, 12]} style={{ marginBottom: 14 }}>
                                <Col xs={12} sm={8}><Card><Statistic title="Séances" value={seances.length} prefix={<ClusterOutlined />} valueStyle={{ color: PRIMARY }} /></Card></Col>
                                <Col xs={12} sm={8}><Card><Statistic title="PV joints" value={nbPv} prefix={<FileTextOutlined />} valueStyle={{ color: "#0891b2" }} /></Card></Col>
                                <Col xs={24} sm={8}><Card><Statistic title="Participants" value={participants.length} prefix={<TeamOutlined />} valueStyle={{ color: "#16a34a" }} /></Card></Col>
                            </Row>
                            <Card bordered={false} style={{ borderRadius: 12 }}>
                                <Tabs items={[
                                    {
                                        key: "seances", label: "Séances & PV",
                                        children: (
                                            <>
                                                {isMembre() && <div style={{ textAlign: "right", marginBottom: 10 }}><Button type="primary" icon={<PlusOutlined />} onClick={creerSeance}>Nouvelle séance</Button></div>}
                                                <Table rowKey="codeId" size="small" columns={seanceColumns} dataSource={seances} scroll={{ x: "max-content" }} pagination={{ pageSize: 10 }}
                                                    locale={{ emptyText: <Empty description="Aucune séance de production" /> }} />
                                            </>
                                        ),
                                    },
                                    {
                                        key: "participants", label: "Participants",
                                        children: <ParticipantsPanelProd participants={participants} compagnies={compagnies} onChange={charger} />,
                                    },
                                ]} />
                            </Card>
                        </div>
                        <Footer />
                    </div>
                </div>
            </div>
        </div>
    );
};

const ParticipantsPanelProd = ({ participants, compagnies, onChange }) => {
    const pool = isPool();
    const [org, setOrg] = useState(pool ? undefined : myOrgCode());
    const [users, setUsers] = useState([]);
    const [selUser, setSelUser] = useState(undefined);
    const [saving, setSaving] = useState(false);

    const chargerUsers = (code) => {
        if (!code) { setUsers([]); return; }
        axiosInstance.get(`/api/utilisateur/organisme/code/${code}`).then((r) => setUsers(r.data || [])).catch(() => setUsers([]));
    };
    useEffect(() => { if (!pool) chargerUsers(myOrgCode()); }, [pool]);

    const ajouter = () => {
        if (!org || !selUser) { message.warning("Compagnie et utilisateur requis."); return; }
        setSaving(true);
        axiosInstance.post("/api/commission-technique/participants", { organisme: org, userCodeId: selUser, type: TYPE })
            .then(() => { message.success("Participant ajouté."); setSelUser(undefined); onChange && onChange(); })
            .catch((e) => message.error(e?.response?.data?.message || "Échec."))
            .finally(() => setSaving(false));
    };
    const retirer = (p) => axiosInstance.delete(`/api/commission-technique/participants/${p.codeId}`)
        .then(() => { message.success("Participant retiré."); onChange && onChange(); })
        .catch((e) => message.error(e?.response?.data?.message || "Échec."));

    const visibles = pool ? participants : participants.filter((p) => p.organismeCode === myOrgCode());

    return (
        <>
            <Card size="small" style={{ marginBottom: 12, background: "#fafbff" }}>
                <Space wrap align="end">
                    {pool && (
                        <div>
                            <div style={{ fontSize: 12, color: "#888" }}>Compagnie</div>
                            <Select showSearch optionFilterProp="label" style={{ width: 240 }} placeholder="Compagnie"
                                value={org} onChange={(v) => { setOrg(v); setSelUser(undefined); chargerUsers(v); }}
                                options={compagnies.map((c) => ({ value: c.code, label: c.raisonSociale || c.code }))} />
                        </div>
                    )}
                    <div>
                        <div style={{ fontSize: 12, color: "#888" }}>Utilisateur</div>
                        <Select showSearch optionFilterProp="label" style={{ width: 280 }} placeholder="Utilisateur"
                            value={selUser} onChange={setSelUser}
                            options={users.map((u) => ({ value: u.codeId, label: `${u.personne ? `${u.personne.nom || ""} ${u.personne.prenom || ""}`.trim() : u.username}` }))} />
                    </div>
                    <Button type="primary" icon={<UserAddOutlined />} loading={saving} onClick={ajouter}>Ajouter</Button>
                </Space>
            </Card>
            <Table rowKey="codeId" size="small" dataSource={visibles} pagination={{ pageSize: 15 }} scroll={{ x: "max-content" }}
                columns={[
                    { title: "Compagnie", dataIndex: "organismeNom", render: (v, r) => v || r.organismeCode || "-" },
                    { title: "Participant", dataIndex: "userNom", render: (v, r) => v || r.userCodeId },
                    {
                        title: "", key: "x", width: 60, render: (_, p) => (pool || p.organismeCode === myOrgCode()) ? (
                            <Popconfirm title="Retirer ce participant ?" onConfirm={() => retirer(p)} okText="Oui" cancelText="Non">
                                <Button size="small" danger type="text" icon={<DeleteOutlined />} />
                            </Popconfirm>
                        ) : null,
                    },
                ]}
                locale={{ emptyText: <Empty description="Aucun participant" /> }} />
        </>
    );
};

export default CommissionProduction;
